import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { subscriptionEventMetaData } from '../../shared/constants/intercom-event-metadata-types';
import { IntegrationService } from '../../shared/services/integration.service';
import { environment } from '../../../environments/environment';
import { AppState } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { Integration } from '../../shared/models/integration.model';
import { IntercomService } from '../../shared/services/intercom.service';
import { BaseWizardService } from '../../modules/wizards/base-wizard.service';
import { modalMessageHistoryCancel, modalTitleHistoryCancel } from '../../shared/constants/modal-titles-and-messages';
import { modalsClose, modalsOpen } from '../store/actions/modals.action';
import { observableToPromise } from '../functions/utility';

@Injectable({
  providedIn: 'root'
})
export class HistoryService {
  modalStatus$ = new Subject<string>();

  constructor(
    private http: HttpClient,
    private integrationService: IntegrationService,
    private intercomService: IntercomService,
    protected store$: Store<AppState>,
    protected wizardService: BaseWizardService
  ) { }

  async requestHistory(subscriptionId: number, integrationId: number, subProductId: string,
    historyApiStartDate: string, historyApiEndDate: string, firstRunTimeUtc, stageId: string | null = null, marketplaceId: string | null = null): Promise<void> {

    const delay = ms => new Promise(res => setTimeout(res, ms));
    await delay(2000);

    const integration = this.integrationService.findIntegrationFromId(integrationId);
    const payload = {
      data: {
        type: 'HistoryTransaction',
        attributes: {
          product_id: integrationId,
          start_date: historyApiStartDate,
          start_time: firstRunTimeUtc,
          end_date: historyApiEndDate,
          is_primary: false
        }
      }
    };

    if(stageId !== null) {
      payload.data.attributes['stage_id'] = stageId;
    }

    if(marketplaceId !== null) {
      payload.data.attributes['extra_context'] = { marketplace_ids: [marketplaceId] };
    }

    try {

      const requestUrl = `${environment.openbridgeApiUris.service}/service/history/${environment.openbridgeApiUris.partialIdentifierTwo}/history/${subscriptionId}`;
      const response = await observableToPromise(this.http.post(requestUrl, payload));

      // Intercom history request success event
      this.dispatchIntercomSubscriptionEvent(integrationId, subProductId, subscriptionId, 'request', 'success');      
    }
    catch (error) {
      this.modalStatus$.next('modal-history-request-completed');

      // Intercom history request failure event
      this.dispatchIntercomSubscriptionEvent(integrationId, subProductId, subscriptionId, 'request', 'failure', error);

      throw JSON.stringify(error, null, 2);
    }
  }

  async getProductHistoryInformation(): Promise<any> {
    return await observableToPromise(this.http.get(`${environment.openbridgeApiUris.service}/service/history/${environment.openbridgeApiUris.partialIdentifierTwo}/history/meta/max-request`));
  }

  async cancelHistory(subscriptionId: string, integrationId: number, subProductId: string): Promise<void> {
    this.store$.dispatch(modalsOpen({
      modalType: 'progress',
      title: modalTitleHistoryCancel,
      message: `${modalMessageHistoryCancel} ${subscriptionId}`,
      progress: 0
    }));

    const payload = {
      "data": {
        "type": "HistoryTransaction",
        "id": subscriptionId,
        "attributes": { 
          "status": "cancelled"
        }
      }
    };

    const response = await observableToPromise(this.http.patch(`${environment.openbridgeApiUris.service}/service/history/${environment.openbridgeApiUris.partialIdentifierTwo}/history/status/${subscriptionId}`, payload));

    // Intercom history cancellation success.
    this.dispatchIntercomSubscriptionEvent(integrationId, subProductId, subscriptionId, 'cancel', 'success');

    this.store$.dispatch(modalsClose());
  }

  dispatchIntercomSubscriptionEvent(productId: any, subProductId: string, subscriptionId: any, historyMode: 'request' | 'cancel', status: 'success' | 'failure', error?: any | null): void {
    let integration: Integration | null = null;

    if(subProductId && subProductId !== 'default' ) {
      integration = this.integrationService.findIntegrationFromIdAndSubproductId(productId, subProductId);
    } 
    else {
      integration = this.integrationService.findIntegrationFromId(productId);
    }
    
    if(integration) {
      const subProductId = integration.subProductId ? integration.subProductId : 'default';      
      const productName = integration.brand + ' ' + integration.name;
      const eventDescription = 'history ' + historyMode + ' ' + status + ' event';
        
      const metaData: subscriptionEventMetaData = {
        productType: integration.type,
        mode: historyMode,
        eventDescription: eventDescription,
        isAbandon: false,
        productId: productId,
        subProductId: subProductId,
        productName: productName,
        subscriptionId: subscriptionId,
        status: status,
        error: JSON.stringify(error),
      };
  
      this.intercomService.dispatchIntercomSubscriptionEvent(metaData); 
      
    }
  }
}
